<template>
  <div class="w-full">
    <div
      v-if="loading"
      class="flex items-center justify-center p-8"
    >
      <ProgressSpinner />
    </div>
    <div
      v-else-if="loadError"
      class="p-4 text-surface-600 dark:text-surface-400"
    >
      {{ loadError }}
    </div>
    <div
      v-else-if="guideArray.length === 0"
      class="p-4 text-surface-600 dark:text-surface-400"
    >
      Guide not found.
    </div>
    <WikiGuide
      v-else
      :item="guideArray"
      :title="guideArray[0]?.title"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import WikiGuide from '@/components/shared/wikiGuides/WikiGuides.vue';
import { getGuide, resolveWikiImageUrl } from '@/services/wikiService';

const props = defineProps({
  /** Optional pre-fetched guide (API response data object). If not provided, component fetches by route.params.id */
  guideData: { type: Object, default: null },
});

const emit = defineEmits(['loaded']);

const route = useRoute();
const loading = ref(!props.guideData);
const loadError = ref(null);
const fetchedGuide = ref(null);

const guide = computed(() => props.guideData ?? fetchedGuide.value);

/** Map API guide object to array format expected by WikiGuides: [header, ...steps] */
function guideToItemArray(g) {
  if (!g) return [];
  const header = {
    id: g.id,
    title: g.title || 'Guide',
    time: g.time || '0min',
    difficulty: g.difficulty || 'easy',
    tools: [],
    parts: [],
  };
  const steps = (g.steps || []).map((s) => ({
    id: s.id,
    title: s.title || '',
    text: s.text || '',
    image: resolveWikiImageUrl(s.image_url) || '',
  }));
  return [header, ...steps];
}

const guideArray = computed(() => guideToItemArray(guide.value));

async function loadGuide(id) {
  if (!id) return;
  loading.value = true;
  loadError.value = null;
  try {
    const res = await getGuide(id);
    const data = res?.data ?? null;
    fetchedGuide.value = data;
    if (data) emit('loaded', data);
  } catch (e) {
    loadError.value = e.message || 'Failed to load guide';
    fetchedGuide.value = null;
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  if (!props.guideData) {
    loadGuide(route.params.id);
  }
});

watch(() => route.params.id, (id) => {
  if (!props.guideData && id) {
    loadGuide(id);
  }
});
</script>

<style scoped></style>
